/* eslint-disable react-refresh/only-export-components */

import { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { User } from 'firebase/auth';
import type { Lesson } from '../types/lesson';
import type { OriginalVideo, VideoEditDraft, VideoContext } from '../types/videoEdit';
import type { VideoEditorDraftData } from '../components/LessonVideoEditor';
import { useAuth } from './AuthContext';
import {
  subscribeToPlayerLessons,
  subscribeToCoachLessons,
  addLessonReview as addLessonReviewService,
} from '../services/lessons';
import {
  submitLessonDraft as submitLessonDraftService,
  publishAndCompleteDraft as publishAndCompleteDraftService,
  getOrCreateOriginalVideo,
} from '../services/lessonSubmission';

// ─── Types ──────────────────────────────────────────────

interface ActionState {
  loading: boolean;
  error: string | null;
}

interface LessonsByStatus {
  reserved: Lesson[];
  inProgress: Lesson[];
  delivered: Lesson[];
}

interface LessonContextType {
  // Core state
  lessons: Lesson[];
  loading: boolean;
  error: string | null;

  // Derived / computed
  lessonsByStatus: LessonsByStatus;
  activeLessons: Lesson[];
  getLessonsForConversation: (conversationId: string) => Lesson[];
  getLessonById: (lessonId: string) => Lesson | undefined;

  // Selected lesson (stays in sync with real-time data)
  selectedLesson: Lesson | null;
  selectLesson: (lessonOrId: Lesson | string | null) => void;

  // Draft tracking
  lessonsWithDrafts: Set<string>;
  markLessonHasDraft: (lessonId: string) => void;

  // Submission state (specifically for lesson submission pipeline)
  submitting: boolean;

  // Actions
  actionState: ActionState;
  addLessonReview: (lessonId: string, rating: number, comment?: string) => Promise<void>;
  submitLessonDraft: (
    lesson: Lesson,
    data: VideoEditorDraftData,
    existingOriginalVideo?: OriginalVideo | null,
    existingDraftId?: string,
    onStatusUpdate?: (message: string) => void,
  ) => Promise<{ originalVideo: OriginalVideo; savedDraftId: string }>;
  publishAndCompleteDraft: (
    lesson: Lesson,
    originalVideo: OriginalVideo,
    savedDraft: VideoEditDraft,
    notes?: string,
    onStatusUpdate?: (message: string) => void,
  ) => Promise<void>;
  getOrCreateOriginalVideo: (lesson: Lesson) => Promise<OriginalVideo>;
}

// ─── Context & Hooks ────────────────────────────────────

const LessonContext = createContext<LessonContextType | undefined>(undefined);

export function useLessons() {
  const context = useContext(LessonContext);
  if (!context) {
    throw new Error('useLessons must be used within a LessonProvider');
  }
  return context;
}

/**
 * Convenience hook for components that only need lessons for a specific conversation.
 * Filters from the cached lessons array — no additional Firestore query.
 */
export function useConversationLessons(conversationId: string | undefined) {
  const { getLessonsForConversation, loading } = useLessons();

  const lessons = useMemo(() => {
    if (!conversationId) return [];
    return getLessonsForConversation(conversationId);
  }, [conversationId, getLessonsForConversation]);

  return { lessons, loading };
}

/**
 * Convenience hook for getting a single lesson by ID with real-time updates.
 */
export function useLesson(lessonId: string | undefined) {
  const { getLessonById } = useLessons();
  return useMemo(() => {
    if (!lessonId) return null;
    return getLessonById(lessonId) ?? null;
  }, [lessonId, getLessonById]);
}

// ─── Provider ───────────────────────────────────────────

export function LessonProvider({ children }: { children: ReactNode }) {
  const { currentUser, currentMode } = useAuth();

  // Core state
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Selected lesson (tracked by ID, resolved from lessons array)
  const [selectedLessonId, setSelectedLessonId] = useState<string | null>(null);

  // Draft tracking
  const [lessonsWithDrafts, setLessonsWithDrafts] = useState<Set<string>>(new Set());

  // Submission state (specifically for lesson submission pipeline)
  const [submitting, setSubmitting] = useState(false);

  // Action state
  const [actionState, setActionState] = useState<ActionState>({ loading: false, error: null });

  // ─── Subscription ─────────────────────────────────────

  useEffect(() => {
    if (!currentUser) {
      setLessons([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    let unsubscribe: (() => void) | null = null;

    const handleLessons = (updatedLessons: Lesson[]) => {
      setLessons(updatedLessons);
      setLoading(false);
    };

    try {
      if (currentMode === 'coach') {
        unsubscribe = subscribeToCoachLessons(currentUser.uid, undefined, handleLessons);
      } else {
        unsubscribe = subscribeToPlayerLessons(currentUser.uid, handleLessons);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to subscribe to lessons';
      setError(message);
      setLoading(false);
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [currentUser, currentMode]);

  // ─── Computed Values ──────────────────────────────────

  const lessonsByStatus = useMemo<LessonsByStatus>(() => {
    const reserved: Lesson[] = [];
    const inProgress: Lesson[] = [];
    const delivered: Lesson[] = [];

    for (const lesson of lessons) {
      switch (lesson.status) {
        case 'reserved':
          reserved.push(lesson);
          break;
        case 'in_progress':
          inProgress.push(lesson);
          break;
        case 'delivered':
          delivered.push(lesson);
          break;
      }
    }

    return { reserved, inProgress, delivered };
  }, [lessons]);

  const activeLessons = useMemo(
    () => [...lessonsByStatus.reserved, ...lessonsByStatus.inProgress],
    [lessonsByStatus],
  );

  const getLessonsForConversation = useCallback(
    (conversationId: string) => {
      return lessons.filter((l) => l.conversationId === conversationId);
    },
    [lessons],
  );

  const getLessonById = useCallback(
    (lessonId: string) => {
      return lessons.find((l) => l.id === lessonId);
    },
    [lessons],
  );

  // ─── Selected Lesson ──────────────────────────────────

  const selectedLesson = useMemo(() => {
    if (!selectedLessonId) return null;
    return lessons.find((l) => l.id === selectedLessonId) || null;
  }, [lessons, selectedLessonId]);

  const selectLesson = useCallback((lessonOrId: Lesson | string | null) => {
    if (lessonOrId === null) {
      setSelectedLessonId(null);
    } else if (typeof lessonOrId === 'string') {
      setSelectedLessonId(lessonOrId);
    } else {
      setSelectedLessonId(lessonOrId.id);
    }
  }, []);

  // ─── Draft Tracking ───────────────────────────────────

  const markLessonHasDraft = useCallback((lessonId: string) => {
    setLessonsWithDrafts((prev) => new Set(prev).add(lessonId));
  }, []);

  // ─── Action Helpers ───────────────────────────────────

  const runAction = useCallback(
    async <T,>(fn: (user: User) => Promise<T>): Promise<T> => {
      if (!currentUser) throw new Error('Not authenticated');
      setActionState({ loading: true, error: null });
      try {
        const result = await fn(currentUser);
        setActionState({ loading: false, error: null });
        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Action failed';
        setActionState({ loading: false, error: message });
        throw err;
      }
    },
    [currentUser],
  );

  // ─── Actions ──────────────────────────────────────────

  const handleAddLessonReview = useCallback(
    (lessonId: string, rating: number, comment?: string) =>
      runAction((user) => addLessonReviewService(lessonId, user.uid, rating, comment)),
    [runAction],
  );

  const handleSubmitLessonDraft = useCallback(
    async (
      lesson: Lesson,
      data: VideoEditorDraftData,
      existingOriginalVideo?: OriginalVideo | null,
      existingDraftId?: string,
      onStatusUpdate?: (message: string) => void,
    ) => {
      setSubmitting(true);
      try {
        const result = await runAction(async (user) => {
          const res = await submitLessonDraftService({
            user,
            lesson,
            data,
            existingOriginalVideo,
            existingDraftId,
            onStatusUpdate,
          });
          markLessonHasDraft(lesson.id);
          return res;
        });
        return result;
      } finally {
        setSubmitting(false);
      }
    },
    [runAction, markLessonHasDraft],
  );

  const handlePublishAndCompleteDraft = useCallback(
    async (
      lesson: Lesson,
      originalVideo: OriginalVideo,
      savedDraft: VideoEditDraft,
      notes?: string,
      onStatusUpdate?: (message: string) => void,
    ) => {
      setSubmitting(true);
      try {
        await runAction((user) => {
          const lessonContext: VideoContext = { type: 'lesson', id: lesson.id };
          return publishAndCompleteDraftService({
            user,
            lesson,
            lessonContext,
            originalVideo,
            savedDraft,
            notes,
            onStatusUpdate,
          });
        });
      } finally {
        setSubmitting(false);
      }
    },
    [runAction],
  );

  const handleGetOrCreateOriginalVideo = useCallback(
    (lesson: Lesson) => {
      const lessonContext: VideoContext = { type: 'lesson', id: lesson.id };
      return getOrCreateOriginalVideo(lesson, lessonContext);
    },
    [],
  );

  // ─── Provider Value ───────────────────────────────────

  const value = useMemo<LessonContextType>(
    () => ({
      lessons,
      loading,
      error,
      lessonsByStatus,
      activeLessons,
      getLessonsForConversation,
      getLessonById,
      selectedLesson,
      selectLesson,
      lessonsWithDrafts,
      markLessonHasDraft,
      submitting,
      actionState,
      addLessonReview: handleAddLessonReview,
      submitLessonDraft: handleSubmitLessonDraft,
      publishAndCompleteDraft: handlePublishAndCompleteDraft,
      getOrCreateOriginalVideo: handleGetOrCreateOriginalVideo,
    }),
    [
      lessons,
      loading,
      error,
      lessonsByStatus,
      activeLessons,
      getLessonsForConversation,
      getLessonById,
      selectedLesson,
      selectLesson,
      lessonsWithDrafts,
      markLessonHasDraft,
      submitting,
      actionState,
      handleAddLessonReview,
      handleSubmitLessonDraft,
      handlePublishAndCompleteDraft,
      handleGetOrCreateOriginalVideo,
    ],
  );

  return <LessonContext.Provider value={value}>{children}</LessonContext.Provider>;
}
